import React from 'react';
import { Phone, Calendar, FileText, UserPlus, MessageSquare, Mail, Bell, CheckCircle, Stethoscope, Activity, Heart, Syringe, Pill, Dog, Cat, Mic2 } from 'lucide-react';
import { APP_NAME } from '../config/branding';

const IntegrationHub: React.FC = () => {
    return (
        <div className="relative w-full h-[600px] flex items-center justify-center overflow-hidden select-none bg-gradient-to-b from-white/0 to-white/0">
            {/* Background Wavy Design */}
            <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
                <svg width="100%" height="100%" viewBox="0 0 1000 600" preserveAspectRatio="none" className="w-full h-full">
                    <path d="M0,300 C200,200 400,400 600,300 C800,200 1000,350 1200,300" stroke="url(#grad1)" strokeWidth="2" fill="none" />
                    <path d="M0,350 C250,250 450,450 650,350 C850,250 1050,400 1250,350" stroke="url(#grad1)" strokeWidth="2" fill="none" opacity="0.6" />
                    <path d="M0,400 C300,300 500,500 700,400 C900,300 1100,450 1300,400" stroke="url(#grad1)" strokeWidth="2" fill="none" opacity="0.3" />
                    <defs>
                        <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#3B82F6" />
                            <stop offset="50%" stopColor="#8B5CF6" />
                            <stop offset="100%" stopColor="#EC4899" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>

            {/* 3D Scene Container */}
            <div
                className="relative w-[800px] h-[600px] flex items-center justify-center [perspective:2000px]"
            >
                {/* 
                   Main Group 
                   Tilted "the other way" (Positive Y rotation) to match the reference.
                   Right side recedes, Left side comes forward.
                */}
                <div
                    className="relative w-full h-full flex items-center justify-center"
                    style={{
                        transformStyle: 'preserve-3d',
                        transform: 'rotateX(10deg) rotateY(20deg) rotateZ(-5deg)'
                    }}
                >

                    {/* --- Layer 1: Floating Pills (Now in Front) --- */}

                    {/* "Missed Enrollment" equivalent -> "AI Handling Call" */}
                    <div
                        className="absolute top-[25%] left-[10%] z-40"
                        style={{ transform: 'translateZ(-50px)' }}
                    >
                        <div className="bg-white/90 dark:bg-dark-bg-tertiary/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-orange-100 dark:border-orange-900/50 flex items-center gap-3 animate-float" style={{ animationDelay: '0s' }}>
                            <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                <Phone className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-[10px] text-orange-600 dark:text-orange-400 font-bold uppercase">AI Agent</div>
                                <div className="text-xs font-bold text-gray-800 dark:text-dark-text-primary">Handling Call</div>
                            </div>
                        </div>
                    </div>

                    {/* "Completed" equivalent -> "Appointment Set" */}
                    <div
                        className="absolute bottom-[40%] left-[5%] z-40"
                        style={{ transform: 'translateZ(80px)' }}
                    >
                        <div className="bg-white/90 dark:bg-dark-bg-tertiary/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-green-100 dark:border-green-900/50 flex items-center gap-3 animate-float" style={{ animationDelay: '1s' }}>
                            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                                <CheckCircle className="w-4 h-4" />
                            </div>
                            <div className="text-xs font-bold text-gray-800 dark:text-dark-text-primary">Appointment Set</div>
                        </div>
                    </div>

                    {/* "Processing" equivalent -> "Syncing PIMS" */}
                    <div
                        className="absolute bottom-[20%] left-[15%] z-40"
                        style={{ transform: 'translateZ(80px)' }}
                    >
                        <div className="bg-white/90 dark:bg-dark-bg-tertiary/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-orange-100 dark:border-orange-900/50 flex items-center gap-3 animate-float" style={{ animationDelay: '2s' }}>
                            <div className="w-4 h-4 rounded-full border-2 border-orange-400 border-t-transparent animate-spin" />
                            <div className="text-xs font-bold text-gray-800 dark:text-dark-text-primary">Syncing PIMS</div>
                        </div>
                    </div>

                    {/* NEW CARD: "Transcribing SOAP" - Moved to Far Right over Icon Board */}
                    <div
                        className="absolute top-[23%] left-[67%] z-50"
                        style={{ transform: 'translateZ(110px)' }}
                    >
                        <div className="bg-white/95 dark:bg-dark-bg-tertiary/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-xl border border-purple-100 dark:border-purple-900/50 flex items-center gap-3 animate-float" style={{ animationDelay: '1.5s' }}>
                            <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                                <Mic2 className="w-4 h-4" />
                            </div>
                            <div className="text-xs font-bold text-gray-800 dark:text-dark-text-primary">Transcribing SOAP</div>
                        </div>
                    </div>

                    {/* --- Layer 2: Connecting Lines (SVG) --- */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" style={{ transform: 'translateZ(-10px)' }}>
                        {/* Line from AI Handling Call to Center */}
                        <path d="M 180 200 C 250 200, 300 250, 350 300" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="4 4" />
                        {/* Line from Appointment Set to Center */}
                        <path d="M 120 350 C 200 350, 250 320, 300 300" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="4 4" opacity="0.6" />
                        {/* Line from Center to Grid */}
                        <path d="M 450 300 C 550 300, 550 250, 600 200" stroke="#3B82F6" strokeWidth="2" fill="none" />
                    </svg>


                    {/* --- Layer 3: Main Blue Card (Center-Left) --- */}
                    <div
                        className="absolute left-[35%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-blue-600 to-blue-700 rounded-[2rem] shadow-2xl flex flex-col items-center justify-center z-30 group cursor-pointer transition-transform hover:scale-105 duration-300"
                        style={{
                            transform: 'translateZ(60px)',
                            boxShadow: '20px 20px 50px rgba(37, 99, 235, 0.3), -10px -10px 30px rgba(255,255,255,0.1)'
                        }}
                    >
                        <div className="absolute inset-0 bg-white/10 rounded-[2rem] pointer-events-none" />
                        <div className="absolute -top-6 -right-6 text-yellow-400 animate-bounce-slow">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" /></svg>
                        </div>

                        <img src="/logo_clear_back.png" alt={APP_NAME} className="w-24 h-24 object-contain drop-shadow-lg mb-2" />
                        <span className="text-white font-bold text-xl tracking-tight">{APP_NAME}</span>
                    </div>


                    {/* --- Layer 4: Integration Grid Card (Right) --- */}
                    <div
                        className="absolute right-[5%] top-[15%] w-72 h-80 bg-white dark:bg-dark-bg-secondary rounded-3xl shadow-2xl border border-gray-100 dark:border-dark-border p-6 z-20"
                        style={{
                            transform: 'translateZ(20px) rotateY(-10deg)', // Counter-rotate slightly to face viewer more but still tilted
                            boxShadow: '-20px 20px 60px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div className="grid grid-cols-3 gap-4 h-full content-center">
                            {[
                                { icon: <MessageSquare />, color: "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400" },
                                { icon: <Mail />, color: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" },
                                { icon: <Calendar />, color: "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400" },
                                { icon: <Bell />, color: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400" },
                                { icon: <CheckCircle />, color: "bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" },
                                { icon: <Stethoscope />, color: "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400" },
                                { icon: <Activity />, color: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400" },
                                { icon: <Heart />, color: "bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400" },
                                { icon: <Dog />, color: "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400" },
                            ].map((item, i) => (
                                <div key={i} className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center shadow-sm hover:scale-110 transition-transform duration-200`}>
                                    {React.cloneElement(item.icon as React.ReactElement<any>, { className: "w-7 h-7" })}
                                </div>
                            ))}
                        </div>

                        {/* Glass Reflection */}
                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-white/40 dark:from-white/10 via-transparent to-transparent pointer-events-none" />
                    </div>

                </div>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
                .animate-float {
                    animation: float 4s ease-in-out infinite;
                }
                .animate-bounce-slow {
                    animation: bounce 3s infinite;
                }
            `}</style>
        </div>
    );
};

export default IntegrationHub;
